<template>
  <div>
    <!-- Placeholder poster -->
    <slot></slot>

    <!-- If there is a poster, let's show the panel -->
    <div
      v-if="placeholderPoster"
      ref="infoPanel"
      :aria-hidden="ariaHidden"
      :class="{ 'videoComponent__infoPanel--transparent': infoPanelText === '' }"
      class="videoComponent__infoPanel"
    >
      <button
        v-if="!hasYoutubeParams && showPlayButton"
        :aria-label="playIconAltText"
        class="videoComponent__playButton"
        @click.once="play"
      >
        <img
          :alt="playIconAltText"
          class="videoComponent__playButtonIcon"
          src="/frontend/static/images/play.svg"
          aria-hidden="true"
        />
      </button>
      <span v-if="infoPanelText" class="videoComponent__infoPanelText">{{ infoPanelText }}</span>
    </div>

    <button
      ref="videoContainer"
      class="embedResponsive__item_container embedResponsive_focus"
      tabindex="-1"
      :aria-label="playAltText"
      @keydown.space.prevent="toggleVideo"
    >
      <div v-if="hasVideoId" :id="playerId" class="embedResponsive__item"></div>
      <button v-if="hasPlayIcon" class="videoComponent__control" @click.prevent="toggleVideo">
        <span class="is-sr-only">{{ isVideoPlaying ? 'play' : 'pause' }}</span>
        <svg aria-hidden="true" class="icon">
          <use
            xmlns:xlink="http://www.w3.org/1999/xlink"
            :xlink:href="`#${isVideoPlaying ? 'media-play-icon' : 'media-pause-icon'}`"
          ></use>
        </svg>
      </button>
    </button>
  </div>
</template>

<script>
import { AnalyticsHandler } from '@Foundation';
import { YOUTUBE_SCRIPT_NAME, YOUTUBE_SCRIPT_URL } from './settings';
import { loadscript } from '@Foundation/directives/loadscript/loadscript';
import { mixinSetupYouTube } from './mixins/setupYouTube';
import { mixinTryPlayVideo } from './mixins/tryPlayVideo';
import { mixinSetupPlayerAndBindToEvents } from './mixins/setupPlayerAndBindToEvents';
import { makeVideoAccessible } from '@Foundation/utilities/makeVideoAccessible';
import eventBus from '@refapp/eventbus-js';
import { mapGetters } from 'vuex';
import {
  ACCESSIBILITY_STORE_NAMESPACE,
  GET_SITEWIDE_ANIMATIONS,
} from '@Foundation/accessibilityStore/accessibilityStore';

const YT_EVENTS_MAP = {
  playing: 1,
  paused: 2,
  stopped: 5,
};

export default {
  name: 'VideoPlaceholder',

  mixins: [mixinSetupYouTube, mixinTryPlayVideo, mixinSetupPlayerAndBindToEvents],

  props: {
    infoPanelText: { type: String, default: '' },
    playIconAltText: { type: String, default: 'Play video' },
    tagging: { default: null, type: Object },
    videoId: { type: String, default: '' },
    videoIds: { type: Object, default: () => {} },
    host: { type: String, default: 'https://www.youtube.com' },
    isInformative: { type: Boolean, default: false },
    videoTitle: { type: String, default: '' },
    youtubeExtraParams: { type: Object, default: () => {} },
    playAltText: { type: String, default: 'Play video' },
    pauseAltText: { type: String, default: 'Pause video' },
    hasPlayIcon: { type: Boolean, default: false },
  },

  data() {
    return {
      ariaHidden: false,
      placeholderPoster: null,
      placeholderPosterBeenShown: false,
      player: null,
      video: null,
      mobileScreenWidthEnd: 998,
      isVideoPlaying: !this.youtubeExtraParams?.autoplay,
    };
  },

  computed: {
    hasVideoId() {
      return this.video !== '';
    },
    hasYoutubeParams() {
      return this.youtubeExtraParams && Object.keys(this.youtubeExtraParams).length;
    },
    shouldSetupPlayer() {
      return (
        this.hasVideoId &&
        ((!(this.placeholderPoster && this.placeholderPosterBeenShown) &&
          !this.hasPlayIcon &&
          this.hasYoutubeParams) ||
          this.youtubeExtraParams?.autoplay)
      );
    },
    shouldAnimatePlaceholderOnLoad() {
      return (
        this.placeholderPoster &&
        !this.placeholderPosterBeenShown &&
        this.youtubeExtraParams?.autoplay
      );
    },
    shouldAnimatePlaceholder() {
      return this.placeholderPoster && !this.placeholderPosterBeenShown;
    },
    videoIFrame() {
      return this.player.getIframe();
    },
    showPlayButton() {
      return this.hasVideoId && this.hasPlaceholderPoster && !this.youtubeExtraParams?.autoplay;
    },
    hasPlaceholderPoster() {
      return !!this.$slots.default();
    },
    isAnimationEnabled() {
      return this[GET_SITEWIDE_ANIMATIONS]();
    },
    isMobile() {
      return window.innerWidth <= this.mobileScreenWidthEnd;
    },
    playerId() {
      return `player-${this.video}-${Math.random().toString(36).slice(2)}`;
    },
  },

  mounted() {
    this.placeholderPoster = this.$el.querySelector('.videoComponent__placeholder');
    this.initializeVideoId();

    this.$nextTick(async () => {
      /* istanbul ignore next */
      if (this.shouldSetupPlayer) {
        await this.setupPlayerWithEventListeners();
      }
    });
  },

  methods: {
    initializeVideoId() {
      this.video = this.videoId || (this.isMobile ? this.videoIds.mobile : this.videoIds.desktop);
    },

    async setupPlayerWithEventListeners() {
      await this.setupPlayer();
      this.setupEventListeners();
    },

    setupEventListeners() {
      eventBus.on('livestream::play', this.player.playVideo);
      eventBus.on('livestream::pause', this.player.pauseVideo);
      eventBus.on('youtube::state-change', this.handleYoutubeStateChange);
    },

    handleYoutubeStateChange(event) {
      this.updateVideoPlayingState(event);

      if (this.shouldAnimatePlaceholderOnLoad) {
        this.animatePlaceholderPosterAndInfoPanel();
      }
    },

    updateVideoPlayingState(event) {
      const embedElement = this.$el.querySelector('.embedResponsive__item');
      if (!embedElement || embedElement.id !== event.target.g.id) {
        return;
      }

      if (event.data === 1) {
        this.isVideoPlaying = false;
      } else if (event.data === 2) {
        this.isVideoPlaying = true;
      }
    },

    ...mapGetters(ACCESSIBILITY_STORE_NAMESPACE, [GET_SITEWIDE_ANIMATIONS]),
    loadYouTubeScript() {
      loadscript.beforeMount(null, {
        value: {
          name: YOUTUBE_SCRIPT_NAME,
          url: YOUTUBE_SCRIPT_URL,
        },
      });
    },

    play() {
      if (this.tagging) {
        const { action, category, label } = this.tagging;

        AnalyticsHandler.getAnalyticsHandler().push({
          action,
          category,
          label,
          type: 'userActionEvent',
        });
      }

      this.setupPlayer();
    },

    async setupPlayer() {
      this.loadYouTubeScript();
      /* istanbul ignore next */
      return this.setupPlayerAndBindToEvents({
        enablejsapi: 1,
        autoplay: this.isAnimationEnabled,
        ...this.youtubeExtraParams,
      })
        .then(() => {
          this.tryPlayVideo(this.player, false, 500, this.youtubeExtraParams?.autoplay);
          makeVideoAccessible(this.videoIFrame, this.isInformative, this.videoTitle);

          const isUsingKey = document.querySelector('.isUsingKeyboard');
          if (isUsingKey) {
            this.setContainerFocus();
          }
        })
        .then(() => {
          if (this.shouldAnimatePlaceholder) {
            this.animatePlaceholderPosterAndInfoPanel();
          }
        });
    },

    animatePlaceholderPosterAndInfoPanel() {
      /* istanbul ignore else */
      if (this.placeholderPoster) {
        this.placeholderPoster.style.display = 'none';
        this.placeholderPosterBeenShown = true;
      }

      if (this.$refs?.infoPanel) {
        this.$refs.infoPanel.style.display = 'none';
      }
    },

    setContainerFocus() {
      const videoContainer = this.$refs.videoContainer;
      if (!videoContainer || !this.videoIFrame) return;
      videoContainer.setAttribute('tabindex', '0');
      videoContainer.setAttribute('aria-label', this.pauseAltText);
      videoContainer.classList.add('embedResponsive_focus');
      videoContainer.focus();
    },

    async toggleVideo() {
      if (!this.player) {
        await this.setupPlayer();
      }

      try {
        const state = await this.player.getPlayerState();

        switch (state) {
          case YT_EVENTS_MAP.stopped:
            this.animatePlaceholderPosterAndInfoPanel();
            this.player.playVideo();
            break;

          case YT_EVENTS_MAP.paused:
            this.player.playVideo();
            break;

          case YT_EVENTS_MAP.playing:
            this.player.pauseVideo();
            break;

          default:
            console.warn('Unexpected player state:', state);
            return;
        }

        this.isVideoPlaying = !this.isVideoPlaying;

        this.$refs.videoContainer?.setAttribute(
          'aria-label',
          this.isVideoPlaying ? this.pauseAltText : this.playAltText
        );
      } catch (error) {
        console.error('Error toggling video:', error);
      }
    },
  },
};
</script>
